/**
 * Videocall Audio Utilities
 *
 * Pure audio processing helpers used by the videocall module.
 * No closure dependencies — all functions work with standard APIs only.
 */

/**
 * Convert a Blob to a base64 data URI string (without the data: prefix).
 */
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Apply fade-in to the beginning of a Float32Array.
 */
export function applyFadeIn(float32, fadeMs = 5, sampleRate = 16000) {
    const fadeSamples = Math.floor((fadeMs / 1000) * sampleRate);
    const len = float32.length;
    for (let i = 0; i < fadeSamples && i < len; i++) {
        float32[i] *= i / fadeSamples;
    }
    return float32;
}

/**
 * Apply fade-out to the end of a Float32Array.
 */
export function applyFadeOut(float32, fadeMs = 5, sampleRate = 16000) {
    const fadeSamples = Math.floor((fadeMs / 1000) * sampleRate);
    const len = float32.length;
    for (let i = 0; i < fadeSamples && i < len; i++) {
        float32[len - fadeSamples + i] *= 1 - i / fadeSamples;
    }
    return float32;
}

/**
 * Resample a Float32Array from originalRate to targetRate using linear interpolation.
 */
export function resampleFloat32(float32, originalRate, targetRate) {
    if (originalRate === targetRate) return float32;

    const ratio = targetRate / originalRate;
    const newLength = Math.round(float32.length * ratio);
    const resampled = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
        const origIndex = i / ratio;
        const low = Math.floor(origIndex);
        const high = Math.min(Math.ceil(origIndex), float32.length - 1);
        const weight = origIndex - low;
        resampled[i] = float32[low] * (1 - weight) + float32[high] * weight;
    }

    return resampled;
}

/**
 * Decode a base64 string into an Int16Array.
 */
export function base64ToInt16(base64) {
    const binary = atob(base64);
    const buf = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
    return new Int16Array(buf);
}

/**
 * Convert an Int16Array to a Float32Array (normalized to [-1.0, 1.0]).
 */
export function int16ToFloat32(int16) {
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;
    return float32;
}

/**
 * Convert an AudioBuffer to a WAV Blob at the target sample rate.
 */
export function audioBufferToWav(audioBuffer, targetSampleRate = 24000) {
    let samples;
    if (audioBuffer.sampleRate !== targetSampleRate) {
        const ratio = targetSampleRate / audioBuffer.sampleRate;
        const newLength = Math.round(audioBuffer.length * ratio);
        const resampled = new Float32Array(newLength);
        const channelData = audioBuffer.getChannelData(0);

        for (let i = 0; i < newLength; i++) {
            const srcIndex = i / ratio;
            const low = Math.floor(srcIndex);
            const high = Math.min(Math.ceil(srcIndex), channelData.length - 1);
            const weight = srcIndex - low;
            resampled[i] = channelData[low] * (1 - weight) + channelData[high] * weight;
        }
        samples = resampled;
    } else {
        samples = audioBuffer.getChannelData(0);
    }

    // Convert float32 to int16
    const int16 = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    // Create WAV file
    const buffer = new ArrayBuffer(44 + int16.length * 2);
    const dataView = new DataView(buffer);

    const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
            dataView.setUint8(offset + i, string.charCodeAt(i));
        }
    };

    writeString(0, 'RIFF');
    dataView.setUint32(4, 36 + int16.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    dataView.setUint32(16, 16, true);
    dataView.setUint16(20, 1, true);
    dataView.setUint16(22, 1, true);
    dataView.setUint32(24, targetSampleRate, true);
    dataView.setUint32(28, targetSampleRate * 2, true);
    dataView.setUint16(32, 2, true);
    dataView.setUint16(34, 16, true);
    writeString(36, 'data');
    dataView.setUint32(40, int16.length * 2, true);

    for (let i = 0; i < int16.length; i++) {
        dataView.setInt16(44 + i * 2, int16[i], true);
    }

    return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Convert raw PCM16 Uint8Array data to a WAV ArrayBuffer.
 */
export function pcm16ToWav(pcm16Data, sampleRate, numChannels) {
    const dataLength = pcm16Data.length;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);

    new Uint8Array(buffer, 44).set(pcm16Data);

    return buffer;
}
